import './applySearchVehiclePage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { ThemeProvider } from '@mui/material/styles';
import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { SearchVehicle } from '../../../store/applyStore/enums/SearchVehicle';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const isPlateOptionEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_PLATE_TO_VIN_SERVICE,
  );

  const { searchVehicle, applicationType, setSearchVehicle } = useApplyStore();

  const previousStep = (): void => {
    navigate(previous());
  };

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      searchVehicleValue: searchVehicle,
    },
  });

  const radioOptionClicked = (
    event: RadioOptionClickEvent<SearchVehicle>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const searchVehicleValue = event.target.value;

    setIsInitialSelection(isEmpty(searchVehicle) && isValidClickEvent(event));

    setSearchVehicle(searchVehicleValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { searchVehicleValue } = values;

    if (searchVehicleValue) {
      navigate(next());
    }
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <div className="apply-search-vehicle-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applySearchVehicle.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applySearchVehicle.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={locator.pages.applySearchVehicle.header.drawerButton}
              type="button"
              onClick={(): void => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applySearchVehicle.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.applySearchVehicle.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applySearchVehicle.form.h1}>
                  {t('ApplySearchVehiclePage.title')}
                </h1>
                <h2 data-testid={locator.pages.applySearchVehicle.form.h2}>
                  {t('ApplySearchVehiclePage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <RadioGroup
                    name="searchVehicleValue"
                    options={[
                      {
                        label: t('ApplySearchVehiclePage.byVin'),
                        value: SearchVehicle.BY_VIN,
                        testId:
                          locator.pages.applySearchVehicle.form.radioOption1,
                      },
                      {
                        label: t('ApplySearchVehiclePage.byLicensePlate'),
                        value: SearchVehicle.BY_LICENSE_PLATE,
                        condition:
                          applicationType !== ApplicationType.PURCHASE &&
                          isPlateOptionEnabled,
                        testId:
                          locator.pages.applySearchVehicle.form.radioOption2,
                      },
                      {
                        label: t('ApplySearchVehiclePage.byMakeModel'),
                        value: SearchVehicle.BY_MAKE_MODEL,
                        testId:
                          locator.pages.applySearchVehicle.form.radioOption3,
                      },
                    ]}
                    register={register}
                    testId={
                      locator.pages.applySearchVehicle.form.radioGroupRoot
                    }
                    watch={watch}
                    onClick={radioOptionClicked}
                  />
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <Button
                    color="primary"
                    data-testid={
                      locator.pages.applySearchVehicle.form.submitButton
                    }
                    disabled={isEmpty(searchVehicle)}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('ApplySearchVehiclePage.continue')}
                  </Button>
                </div>
              </div>
              <footer
                data-testid={locator.pages.applySearchVehicle.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplySearchVehiclePage';
